<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center py-3">
      <div class="d-flex align-center">
        <v-icon
          color="primary"
          class="mr-2"
        >
          {{ icons.mdiChartBar }}
        </v-icon>
        <span>RFM分析</span>
      </div>
      <v-btn
        small
        outlined
        color="primary"
        :loading="isLoading"
        @click="showDetailDialog = true"
      >
        詳細を見る
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <template v-else>
        <!-- サマリー -->
        <div class="d-flex justify-space-around mb-4">
          <div class="text-center">
            <div class="text-h4 font-weight-bold primary--text">
              {{ summary.analyzedCustomers }}
            </div>
            <div class="text-caption">
              分析対象
            </div>
          </div>
          <div class="text-center">
            <div class="text-h4 font-weight-bold success--text">
              {{ vipCount }}
            </div>
            <div class="text-caption">
              VIP顧客
            </div>
          </div>
          <div class="text-center">
            <div class="text-h4 font-weight-bold warning--text">
              {{ atRiskCount }}
            </div>
            <div class="text-caption">
              離反リスク
            </div>
          </div>
        </div>

        <!-- セグメント分布 -->
        <div class="segment-list">
          <div
            v-for="segment in segmentDistribution"
            :key="segment.name"
            class="d-flex align-center justify-space-between py-1"
          >
            <div class="d-flex align-center">
              <v-avatar
                :color="segment.color"
                size="12"
                class="mr-2"
              />
              <span>{{ segment.label }}</span>
            </div>
            <span class="font-weight-bold">{{ segment.count }}名</span>
          </div>
        </div>
      </template>
    </v-card-text>

    <!-- 詳細ダイアログ -->
    <v-dialog
      v-model="showDetailDialog"
      max-width="900"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>RFM分析詳細</span>
          <v-btn
            icon
            @click="showDetailDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          期間: {{ formatDate(startDate) }} 〜 {{ formatDate(endDate) }}
        </v-card-subtitle>
        <v-card-text>
          <!-- セグメントフィルター -->
          <v-chip-group
            v-model="selectedSegment"
            active-class="primary--text"
            class="mb-4"
          >
            <v-chip
              value=""
              filter
            >
              全て
            </v-chip>
            <v-chip
              v-for="segment in segmentDistribution"
              :key="segment.name"
              :value="segment.name"
              :color="segment.color"
              filter
              outlined
            >
              {{ segment.label }}({{ segment.count }})
            </v-chip>
          </v-chip-group>

          <!-- 顧客テーブル -->
          <v-simple-table dense>
            <template #default>
              <thead>
                <tr>
                  <th>顧客名</th>
                  <th class="text-center">
                    R
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          x-small
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ icons.mdiInformation }}
                        </v-icon>
                      </template>
                      <span>Recency（最終来店からの日数）</span>
                    </v-tooltip>
                  </th>
                  <th class="text-center">
                    F
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          x-small
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ icons.mdiInformation }}
                        </v-icon>
                      </template>
                      <span>Frequency（来店頻度）</span>
                    </v-tooltip>
                  </th>
                  <th class="text-center">
                    M
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          x-small
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ icons.mdiInformation }}
                        </v-icon>
                      </template>
                      <span>Monetary（購入金額）</span>
                    </v-tooltip>
                  </th>
                  <th class="text-center">
                    合計
                  </th>
                  <th>セグメント</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="customer in filteredCustomers"
                  :key="customer.customerId"
                  class="cursor-pointer"
                  @click="handleCustomerClick(customer)"
                >
                  <td>{{ customer.customerName }}</td>
                  <td class="text-center">
                    <v-chip
                      x-small
                      :color="getScoreColor(customer.rScore)"
                      text-color="white"
                    >
                      {{ customer.rScore }}
                    </v-chip>
                  </td>
                  <td class="text-center">
                    <v-chip
                      x-small
                      :color="getScoreColor(customer.fScore)"
                      text-color="white"
                    >
                      {{ customer.fScore }}
                    </v-chip>
                  </td>
                  <td class="text-center">
                    <v-chip
                      x-small
                      :color="getScoreColor(customer.mScore)"
                      text-color="white"
                    >
                      {{ customer.mScore }}
                    </v-chip>
                  </td>
                  <td class="text-center font-weight-bold">
                    {{ customer.rfmScore }}
                  </td>
                  <td>
                    <v-chip
                      small
                      :color="getSegmentColor(customer.segment)"
                      text-color="white"
                    >
                      {{ getSegmentLabel(customer.segment) }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div
            v-if="filteredCustomers.length === 0"
            class="text-center py-4 grey--text"
          >
            該当する顧客がいません
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import { format } from 'date-fns'
import {
  mdiChartBar, mdiClose, mdiInformation,
} from '@mdi/js'
import useRfmAnalysis from '../composables/useRfmAnalysis'

export default {
  name: 'RfmAnalysis',
  props: {
    dateRange: {
      type: Object,
      default: () => ({ start: null, end: null }),
    },
  },
  setup(props, { root, emit }) {
    const {
      customers,
      summary,
      segments,
      loading,
      startDate,
      endDate,
      segmentDistribution,
      vipCustomers,
      atRiskCustomers,
      fetchRfmAnalysis,
      getSegmentLabel,
      getSegmentColor,
      getScoreColor,
    } = useRfmAnalysis(root)

    const showDetailDialog = ref(false)
    const selectedSegment = ref('')

    const isLoading = computed(() => loading.value)
    const vipCount = computed(() => vipCustomers.value.length)
    const atRiskCount = computed(() => atRiskCustomers.value.length)

    const filteredCustomers = computed(() => {
      if (!selectedSegment.value) return customers.value

      return customers.value.filter(c => c.segment === selectedSegment.value)
    })

    const formatDate = dateString => {
      if (!dateString) return '-'
      try {
        return format(new Date(dateString), 'yyyy/MM/dd')
      } catch (e) {
        return dateString
      }
    }

    const loadRfmAnalysis = () => {
      fetchRfmAnalysis({
        start: props.dateRange.start,
        end: props.dateRange.end,
      })
    }

    const handleCustomerClick = customer => {
      showDetailDialog.value = false
      emit('customer-click', customer.customerId)
    }

    // 初回ロード
    loadRfmAnalysis()

    return {
      customers,
      summary,
      segments,
      isLoading,
      startDate,
      endDate,
      segmentDistribution,
      vipCount,
      atRiskCount,
      showDetailDialog,
      selectedSegment,
      filteredCustomers,
      formatDate,
      getSegmentLabel,
      getSegmentColor,
      getScoreColor,
      handleCustomerClick,
      icons: {
        mdiChartBar,
        mdiClose,
        mdiInformation,
      },
    }
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.segment-list {
  max-height: 200px;
  overflow-y: auto;
}
</style>
